/**
 * useTextToSpeech — Stage 10 Voice Output.
 *
 * Browser-native speech synthesis with sentence-by-sentence playback so the
 * user starts hearing Kindra's reply within ~150ms of pressing play, instead
 * of waiting for the entire reply to be passed to the synthesiser. Long
 * paragraphs are split on sentence boundaries and queued.
 *
 * API:
 *   const { supported, speaking, paused, play, pause, resume, stop,
 *           rate, setRate, voice, voices, setVoice } = useTextToSpeech();
 *   play(text)   — starts a fresh utterance queue (cancels any in-flight one).
 *   pause()      — pauses mid-utterance (resume continues).
 *   resume()     — continues paused playback.
 *   stop()       — flushes the queue.
 *
 * Voice selection:
 *   We try to pick a calm, natural English voice on first load. Common picks:
 *   • macOS Safari → "Samantha", "Karen", "Daniel"
 *   • iOS         → "Samantha"
 *   • Android Chrome → "Google US English", "Google UK English Female"
 *   • Windows Edge → "Microsoft Zira Online", "Microsoft Aria Online"
 *   The user can also override via settings.
 *
 * Persistence: rate + voice name persist via localStorage so the experience
 * is consistent across sessions.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const SETTINGS_KEY = 'kindra_voice_settings_v1';

const PREFERRED_VOICE_HINTS = [
  /samantha/i,
  /karen/i,
  /aria/i,
  /jenny/i,
  /google\s+us\s+english/i,
  /google\s+uk\s+english\s+female/i,
  /microsoft\s+aria/i,
  /microsoft\s+jenny/i,
];

const splitSentences = (text) => {
  if (!text) return [];
  // Naïve but effective: split on sentence-ending punctuation followed by
  // whitespace. Filter empties. Keeps emoji, em-dashes, etc intact.
  const parts = text
    .replace(/\s+/g, ' ')
    .split(/(?<=[\.!\?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length ? parts : [text.trim()];
};

const loadSettings = () => {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return {};
    return JSON.parse(raw);
  } catch { return {}; }
};

const saveSettings = (next) => {
  try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(next)); }
  catch { /* localStorage may be blocked in private mode — ignore */ }
};

export const useTextToSpeech = () => {
  const [supported] = useState(() => typeof window !== 'undefined' && 'speechSynthesis' in window);
  const [speaking, setSpeaking] = useState(false);
  const [paused, setPaused] = useState(false);
  const [voices, setVoices] = useState([]);
  const [voiceName, setVoiceName] = useState(() => loadSettings().voiceName || null);
  const [rate, setRateState] = useState(() => loadSettings().rate || 1.0);

  // Sentence queue tracker — refs so we can mutate without re-render.
  const queueRef = useRef([]);
  const cursorRef = useRef(0);
  const cancelledRef = useRef(false);

  // Load + watch voices (some browsers populate them async on the
  // `voiceschanged` event; mobile Safari sometimes never fires it, so we
  // also poll once after a short delay).
  useEffect(() => {
    if (!supported) return undefined;
    const synth = window.speechSynthesis;
    const refresh = () => {
      const list = synth.getVoices() || [];
      setVoices(list);
      // Auto-pick the first preferred voice on first load if none chosen.
      setVoiceName((prev) => {
        if (prev) return prev;
        for (const re of PREFERRED_VOICE_HINTS) {
          const found = list.find((v) => re.test(v.name) && /en/i.test(v.lang || ''));
          if (found) return found.name;
        }
        const anyEn = list.find((v) => /en/i.test(v.lang || ''));
        return anyEn?.name || (list[0]?.name || null);
      });
    };
    refresh();
    synth.onvoiceschanged = refresh;
    const t = setTimeout(refresh, 800);
    return () => { clearTimeout(t); synth.onvoiceschanged = null; };
  }, [supported]);

  const voice = useMemo(
    () => voices.find((v) => v.name === voiceName) || null,
    [voices, voiceName],
  );

  const setVoice = useCallback((name) => {
    setVoiceName(name);
    saveSettings({ voiceName: name, rate });
  }, [rate]);

  const setRate = useCallback((r) => {
    const clamped = Math.max(0.5, Math.min(2.0, r));
    setRateState(clamped);
    saveSettings({ voiceName, rate: clamped });
  }, [voiceName]);

  const playNext = useCallback(() => {
    if (!supported) return;
    if (cancelledRef.current) return;
    const synth = window.speechSynthesis;
    const queue = queueRef.current;
    if (cursorRef.current >= queue.length) {
      setSpeaking(false);
      setPaused(false);
      return;
    }
    const text = queue[cursorRef.current++];
    const u = new SpeechSynthesisUtterance(text);
    if (voice) u.voice = voice;
    u.rate = rate;
    u.pitch = 1.0;
    u.onend = () => playNext();
    u.onerror = () => playNext();
    synth.speak(u);
  }, [supported, voice, rate]);

  const play = useCallback((text) => {
    if (!supported || !text) return;
    const synth = window.speechSynthesis;
    // Hard reset any in-flight queue so we never overlap.
    cancelledRef.current = false;
    try { synth.cancel(); } catch { /* noop */ }
    queueRef.current = splitSentences(text);
    cursorRef.current = 0;
    setSpeaking(true);
    setPaused(false);
    // Start. Use a microtask to give Safari time to flush the cancel above.
    setTimeout(() => playNext(), 30);
  }, [supported, playNext]);

  const pause = useCallback(() => {
    if (!supported) return;
    try { window.speechSynthesis.pause(); setPaused(true); } catch { /* noop */ }
  }, [supported]);

  const resume = useCallback(() => {
    if (!supported) return;
    try { window.speechSynthesis.resume(); setPaused(false); } catch { /* noop */ }
  }, [supported]);

  const stop = useCallback(() => {
    if (!supported) return;
    cancelledRef.current = true;
    try { window.speechSynthesis.cancel(); } catch { /* noop */ }
    queueRef.current = [];
    cursorRef.current = 0;
    setSpeaking(false);
    setPaused(false);
  }, [supported]);

  // Cleanup on unmount.
  useEffect(() => () => { try { window?.speechSynthesis?.cancel(); } catch { /* noop */ } }, []);

  return {
    supported, speaking, paused, voices, voice, voiceName,
    rate, setVoice, setRate,
    play, pause, resume, stop,
  };
};

export default useTextToSpeech;
